/**
 * TrialRibbon — the persistent "where am I in the trial" strip on the creator
 * dashboard (TRIAL_PLAN.md §8.4). Shows the day, the countdown, the exact
 * day-14 total with every commitment itemised, and what happens to the trial
 * credit. Amber in the final 48h, red in the final 24h.
 *
 * A cancelled trial says so plainly here: access to the end, no charge, and
 * the Pro selections are VOID rather than pending (Randy, Oct 2026).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Warning, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useTrial, setTrial, money, trialDate } from "@/lib/trial";
import TrialCreditNote from "@/components/TrialCreditNote";

export default function TrialRibbon() {
  const { trial } = useTrial();
  const nav = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!trial?.live) return null;

  const days = trial.trial_days || 14;
  const hoursLeft = Number(trial.hours_left || 0);
  const dayNo = Math.min(days, Math.max(1, days - Number(trial.days_left || 0) + 1));
  const cancelled = !!trial.cancel_at_period_end;
  const urgency = cancelled ? "calm" : hoursLeft <= 24 ? "red" : hoursLeft <= 48 ? "amber" : "calm";
  const tone = {
    calm: "border-[#4A5D4E]/25 bg-[#E8EBE5]",
    amber: "border-[#B5791F]/30 bg-[#FBF2E2]",
    red: "border-[#A33A3A]/30 bg-[#FBEAEA]",
  }[urgency];
  const accent = { calm: "text-[#4A5D4E]", amber: "text-[#8A5A00]", red: "text-[#A33A3A]" }[urgency];

  const cancel = async () => {
    setBusy(true);
    try {
      const r = await api.post("/trial/cancel", {});
      toast.success(r.data?.message || "Your trial is cancelled.");
      const fresh = await api.get("/trial/status");
      setTrial(fresh.data);
      setConfirming(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel the trial.");
    } finally { setBusy(false); }
  };

  const committed = trial.committed_skus || [];

  return (
    <div className={`siloart-encased rounded-lg border ${tone} px-4 py-3 mb-5`} data-testid="trial-ribbon">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.22em] flex items-center gap-1.5 ${accent}`}>
            {urgency === "calm" ? <Clock size={12} weight="duotone" /> : <Warning size={12} weight="duotone" />}
            <span data-testid="trial-ribbon-day">Day {dayNo} of {days} — full access</span>
          </div>
          <div className="text-sm text-[#1C1B1A] mt-1">
            {cancelled ? (
              <span data-testid="trial-ribbon-cancelled">
                <strong>Cancelled.</strong> You keep everything until {trialDate(trial.ends_at)} and you
                will not be charged.
              </span>
            ) : (
              <>
                {hoursLeft <= 48
                  ? <strong>{hoursLeft <= 1 ? "Less than an hour left" : `${hoursLeft} hours left`}</strong>
                  : <>Ends {trialDate(trial.ends_at)} · {trial.days_left} day{trial.days_left === 1 ? "" : "s"} left</>}
              </>
            )}
          </div>
        </div>

        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">
            {cancelled ? "Day 14 total" : `Day ${days} total`}
          </div>
          <div className="font-serif-display text-2xl tabular-nums text-[#1C1B1A]" data-testid="trial-ribbon-total">
            {cancelled ? money(0) : money(trial.running_total_cents)}
          </div>
        </div>
      </div>

      {/* Itemisation — what that number is made of, never just a number. */}
      <div className="mt-2.5 text-[12px] text-[#4A5D4E] space-y-1" data-testid="trial-ribbon-items">
        <div className="flex items-center justify-between gap-3">
          <span>Creator subscription{cancelled ? " (not starting)" : ""}</span>
          <span className={`tabular-nums ${cancelled ? "line-through text-[#6B6A68]" : ""}`}>
            {money(trial.subscription_cents)}/mo
          </span>
        </div>
        {committed.map((row) => (
          <div key={row.sku} className="flex items-center justify-between gap-3" data-testid={`trial-ribbon-item-${row.sku}`}>
            <span className={cancelled ? "line-through text-[#6B6A68]" : ""}>
              {row.name}{cancelled ? " — void" : ""}
            </span>
            <span className={`tabular-nums ${cancelled ? "line-through text-[#6B6A68]" : ""}`}>
              {money(row.price_cents)}
            </span>
          </div>
        ))}
        {!committed.length && !cancelled && (
          <div className="text-[#6B6A68]">No Pro tools on the bill — they stay free unless you keep them.</div>
        )}
        {cancelled && !!committed.length && (
          <div className="text-[#8A5A00]" data-testid="trial-ribbon-void-note">
            These selections are void. Nothing will be charged for them.
          </div>
        )}
      </div>

      <div className="mt-3">
        <TrialCreditNote trial={trial} tone="light" testId="trial-ribbon-credit-note" />
      </div>

      {!confirming ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => nav("/dashboard/account-settings")}
            data-testid="trial-ribbon-review"
            className="text-xs px-3.5 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] transition-colors"
          >
            Review my day-{days} bill
          </button>
          {!cancelled && (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              data-testid="trial-ribbon-cancel"
              className="text-xs px-3.5 py-2 rounded-md border hairline bg-white text-[#6B6A68] hover:text-[#A33A3A] hover:border-[#A33A3A]/40 transition-colors flex items-center gap-1.5"
            >
              <X size={12} /> Cancel trial
            </button>
          )}
        </div>
      ) : (
        <div className="mt-3 rounded-md border border-[#A33A3A]/25 bg-white px-3 py-2.5" data-testid="trial-cancel-confirm">
          <p className="text-[12px] text-[#1C1B1A]">
            Cancel the trial? You keep every tool until {trialDate(trial.ends_at)}, nothing is charged,
            and any Pro tools you chose are voided. Your silos, library and documents stay exactly where they are.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={cancel}
              disabled={busy}
              data-testid="trial-cancel-yes"
              className="text-xs px-3.5 py-2 rounded-md bg-[#A33A3A] text-white hover:bg-[#8A3030] disabled:opacity-60 transition-colors"
            >
              {busy ? "Cancelling…" : "Yes, cancel it"}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={busy}
              data-testid="trial-cancel-no"
              className="text-xs px-3.5 py-2 rounded-md border hairline bg-white text-[#6B6A68] hover:text-[#1C1B1A] disabled:opacity-60"
            >
              Keep my trial
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
